import React, { useState, useEffect } from 'react';
import './ConversationalSearch.css';
import ConversationalSearchSelectionItem from './ConversationalSearchSelectionItem';
import ConversationalSearchProgressBar from './ConversationalSearchProgressBar';
import SearchResultsContainer from './SearchResultsContainer';
import { useConfig } from "./ConfigContext";
import { searchConversational } from './services/apiService';


const initialFilters = {
    genreCategory: '',
    subgenreCategories: '',
    thematicCategories: '',
};

function formatFilterInfoValue(value) {
    if (!value || (Array.isArray(value) && value.length === 0))
        return '-';
    const getRawLabel = (val) => (val && typeof val === 'object' ? val.title : val);
    const formatEntry = (val, rawLabel) => {
        if (!rawLabel || rawLabel === 'Keine/Egal') return null;
        return val?.score != null ? `${rawLabel} (${Number(val.score).toFixed(2)})` : String(rawLabel);
    };
    if (Array.isArray(value)) {
        const uniqueMap = new Map();
        value.forEach((val) => {
            const rawLabel = getRawLabel(val);
            if (rawLabel && !uniqueMap.has(rawLabel)) {
                uniqueMap.set(rawLabel, formatEntry(val, rawLabel));
            }
        });
        const result = Array.from(uniqueMap.values()).filter(Boolean).join(', ');
        return result || '-';
    }
    return formatEntry(value, getRawLabel(value)) ?? '-';
}

export function getFilterInfo(data) {
    if (!data) {
        return null
    }

    return (
        <>
            <div className='pill-accordion-header'>Folgende Filter wurden bei der Suche angewendet</div>
            <div><span className='pill-accordion-filter'>Genre Kategorie</span>{formatFilterInfoValue(data.genreCategory)}</div>
            <div><span className='pill-accordion-filter'>Subgenre Kategorie</span>{formatFilterInfoValue(data.subgenreCategories)}</div>
            <div><span className='pill-accordion-filter'>Thematische Kategorie</span>{formatFilterInfoValue(data.thematicCategories)}</div>
        </>
    )
}


export default function ConversationalSearch({ requestConfig, setError }) {
    const config = useConfig()
    const [isLoading, setIsLoading] = useState(false);

    const [filters, setFilters] = useState(initialFilters);
    const [userSelection, setUserSelection] = useState(null)
    const [step, setStep] = useState('question');
    const [question, setQuestion] = useState(null);
    const [options, setOptions] = useState([]);
    const [activeFilterKey, setActiveFilterKey] = useState('genreCategory');

    const [query, setQuery] = useState('');
    const [originalQuery, setOriginalQuery] = useState('');
    const [data, setData] = useState([]);
    const [accordionInfo, setAccordionInfo] = useState(null)

    const [fromPosition, setFromPosition] = useState(0);

    const formattedTitle = query.split(',').map(item => item.trim()).filter(item => item !== 'Keine/Egal').join(', ');


    useEffect(() => {
        handleConversationalSearch(initialFilters, null, 0);
    }, []);

    const handleConversationalSearch = async (filtersArg, userSelectionArg, fromPos) => {
        setIsLoading(true);
        resetState()
        try {
            const requestBody = {
                filters: filtersArg,
                ...(userSelectionArg ? { user_selection: userSelectionArg } : {}),
            };
            const data = await searchConversational(config, requestConfig, fromPos, { body: requestBody })

            if (data.type === 'question') {
                setStep('question');
                setQuestion(data.value.text);
                setOptions(data.value.options);
                if (data.value && data.value.filter_key) {
                    setActiveFilterKey(data.value.filter_key);
                }
                if (data.current_filters) {
                    setFilters(data.current_filters);
                } else if (data.filters) {
                    setFilters(data.filters);
                }
            }

            if (data.type === 'results') {
                setStep('results');
                // Compose a query string from the selected filters for display
                let filtersObj = data.filters_used || data.current_filters || {};
                let filterString = Object.values(filtersObj)
                    .filter(Boolean)
                    .join(', ');
                if (!filterString) filterString = 'Gefundene Inhalte';

                setData(data.value.documents || [])
                setQuery(filterString)
                setOriginalQuery(filterString)
                setAccordionInfo(getFilterInfo(data.filters_used))
                if (data.current_filters) {
                    setFilters(data.current_filters);
                } else if (data.filters_used) {
                    setFilters(data.filters_used);
                }
            }
            setIsLoading(false);
        } catch (err) {
            console.error(err)
            setIsLoading(false);
            setError('Failed to fetch error: ' + err.message);
        }
    }

    const handlePreviousPage = async () => {
        const newFromPos = Math.max(0, fromPosition - requestConfig.maxResults);
        setFromPosition(newFromPos);
        await handleConversationalSearch(filters, userSelection, newFromPos)
    };

    const handleNextPage = async () => {
        const newFromPos = fromPosition + requestConfig.maxResults;
        setFromPosition(newFromPos);
        await handleConversationalSearch(filters, userSelection, newFromPos)
    };


    const handleOptionClick = (option) => {
        setUserSelection({
            filter_key: activeFilterKey,
            value: { label: option.label, value: option.value },
        })

        handleConversationalSearch(
            filters,
            {
                filter_key: activeFilterKey,
                value: { label: option.label, value: option.value },
            },
            fromPosition
        );
    };

    const resetState = () => {
        setQuery('')
        setOriginalQuery('')
        setData([])
        setAccordionInfo(null)
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }

    const handleReset = () => {
        resetState()
        setFilters(initialFilters)
        setUserSelection(null)
        setStep('question')
        setQuestion(null)
        setOptions([])
        setActiveFilterKey('genreCategory')
        setFromPosition(0)
        handleConversationalSearch(initialFilters, null, 0)
    };

    return (
        <div className="conversational-search-container">
            {isLoading && (<div className='loading-container'><div className="spinner"></div></div>)}
            {!isLoading && step === 'question' && question && (
                <>
                    <div className="conversational-question" style={{ fontWeight: 'bold', fontSize: '0.8em', marginBottom: 12 }}>
                        {question}
                    </div>
                    <div className="seletion-items-container">
                        {options.map((opt) => (
                            <ConversationalSearchSelectionItem key={opt.value} options={opt} handleClick={handleOptionClick} />
                        ))}
                    </div>
                    <ConversationalSearchProgressBar step={activeFilterKey} />
                </>
            )}
            {!isLoading && data.length ? (
                <SearchResultsContainer
                    items={data}
                    query={formattedTitle}
                    originalQuery={formattedTitle}
                    selectedFeature={'conversational'}
                    aiSearchMode={'conversational'}
                    accordionInfo={accordionInfo}
                    fromPosition={fromPosition}
                    maxResults={requestConfig.maxResults}
                    handlePreviousPage={handlePreviousPage}
                    handleNextPage={handleNextPage}
                    showPagination={true}
                    handleReset={handleReset}
                />
            ) : null}
        </div>
    );
}